import { LEAD_FORMAT_LABELS, LEAD_STATUS_LABELS, LEAD_STATUSES, type LeadFormat, type LeadStatus } from "@cafe/shared";
import { Alert, Badge, Button, DataTable, Drawer, EmptyState, Field, PageHeader, Pagination, SegmentedControl, Select, Skeleton, Textarea, toast } from "@cafe/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Inbox, Mail, Phone, Save } from "lucide-react";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import { api, dt, errorText, http, useCan } from "../lib/api";

export type Lead = {
  id: string; name: string; phone: string; email: string | null; venueName: string; city: string | null; format: LeadFormat; branches: number;
  message: string | null; source: string | null; status: LeadStatus; note: string | null; handledBy: string | null; handledByName: string | null;
  contactedAt: string | null; createdAt: string; updatedAt: string;
};

export const LEAD_TONE: Record<LeadStatus, "warning" | "info" | "primary" | "success" | "neutral"> = { new: "warning", contacted: "info", demo: "primary", won: "success", lost: "neutral" };
const telHref = (p: string) => `tel:${p.replace(/[^\d+]/g, "")}`;

function LeadDrawer({ id, onClose }: { id: string; onClose: () => void }) {
  const can = useCan();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["lead", id], queryFn: () => http.get<Lead>(`/leads/${id}`) });
  const [status, setStatus] = useState<LeadStatus>("new");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const l = q.data;
  useEffect(() => {
    if (!l) return;
    setStatus(l.status);
    setNote(l.note ?? "");
  }, [l]);
  const dirty = !!l && (status !== l.status || note.trim() !== (l.note ?? ""));
  const save = async () => {
    setBusy(true);
    try {
      qc.setQueryData(["lead", id], await http.patch<Lead>(`/leads/${id}`, { status, note: note.trim() || null }));
      void qc.invalidateQueries({ queryKey: ["leads"] });
      void qc.invalidateQueries({ queryKey: ["leads-summary"] });
      toast.success("Заявка сохранена");
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Drawer open onOpenChange={(o) => !o && onClose()} width="md" title={l ? l.venueName : "…"} description={l ? `Заявка от ${dt(l.createdAt)}` : undefined}
      footer={l && can("leads.manage") && <Button leftIcon={<Save />} loading={busy} disabled={!dirty} onClick={() => void save()}>Сохранить</Button>}>
      {q.isError && <Alert tone="danger">{errorText(q.error)}</Alert>}
      {q.isPending && <Skeleton className="h-64" />}
      {l && (
        <div className="flex flex-col gap-5">
          <div className="flex flex-col gap-2 rounded-tile bg-surface-2 px-4 py-3 text-sm">
            <p className="font-medium">{l.name}</p>
            <a className="flex min-h-11 items-center gap-2 text-primary hover:underline" href={telHref(l.phone)}><Phone className="size-4" aria-hidden />{l.phone}</a>
            {l.email && <a className="flex min-h-11 items-center gap-2 text-primary hover:underline" href={`mailto:${l.email}`}><Mail className="size-4" aria-hidden />{l.email}</a>}
          </div>
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
            <dt className="text-muted">Формат</dt><dd>{LEAD_FORMAT_LABELS[l.format] ?? l.format}</dd>
            <dt className="text-muted">Город</dt><dd>{l.city ?? "—"}</dd>
            <dt className="text-muted">Филиалов</dt><dd>{l.branches}</dd>
            {l.source && <><dt className="text-muted">Откуда</dt><dd>{l.source}</dd></>}
            <dt className="text-muted">Обработал</dt><dd>{l.handledByName ? `${l.handledByName} · ${dt(l.updatedAt)}` : "—"}</dd>
          </dl>
          {l.message && (
            <div className="flex flex-col gap-1">
              <p className="text-sm text-muted">Сообщение</p>
              <p className="whitespace-pre-wrap break-words rounded-tile bg-surface-2 px-4 py-3 text-sm">{l.message}</p>
            </div>
          )}
          {can("leads.manage") ? (
            <>
              <Field label="Статус">
                <Select value={status} onValueChange={setStatus} options={LEAD_STATUSES.map((s) => ({ value: s, label: LEAD_STATUS_LABELS[s] }))} />
              </Field>
              <Field label="Заметка" hint="Видна только сотрудникам платформы">
                <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={4} maxLength={2000} placeholder="Например: перезвонить в пятницу после 15:00" />
              </Field>
            </>
          ) : (
            <div className="flex flex-col gap-2 text-sm">
              <Badge tone={LEAD_TONE[l.status]} dot className="self-start">{LEAD_STATUS_LABELS[l.status]}</Badge>
              {l.note && <p className="whitespace-pre-wrap">{l.note}</p>}
            </div>
          )}
        </div>
      )}
    </Drawer>
  );
}

/** Заявки на подключение с сайта Coca: новые сверху; связались → показали систему → подключили или отказ. */
export default function LeadsPage() {
  const [status, setStatus] = useState<LeadStatus | "all">("new");
  const [page, setPage] = useState(1);
  // Открытая заявка — в адресе: на неё ведут тост и ссылка из Telegram
  const [params, setParams] = useSearchParams();
  const open = params.get("lead");
  const setOpen = (id: string | null) => setParams(id ? { lead: id } : {}, { replace: true });
  const list = useQuery({
    queryKey: ["leads", status, page], refetchInterval: 60_000,
    queryFn: () => api<{ items: Lead[]; meta: { total: number } }>("GET", "/leads", undefined, { withMeta: true, query: { status: status === "all" ? undefined : status, page, pageSize: 50 } }),
  });
  return (
    <>
      <PageHeader title="Заявки" description="Заявки на подключение с сайта. Позвоните клиенту и отметьте, что дальше: связались, показали систему, подключили или отказ." />
      <SegmentedControl aria-label="Статус" value={status} onValueChange={(v) => { setStatus(v); setPage(1); }}
        options={[...LEAD_STATUSES.map((s) => ({ value: s, label: LEAD_STATUS_LABELS[s] })), { value: "all" as const, label: "Все" }]} />
      {list.isError && <Alert tone="danger">{errorText(list.error)}</Alert>}
      <DataTable rows={list.data?.items} loading={list.isPending} rowKey={(l) => l.id} onRowClick={(l) => setOpen(l.id)}
        empty={<EmptyState icon={<Inbox />} title={status === "new" ? "Новых заявок нет" : "Заявок нет"} description="Заявки приходят с формы на странице «Партнёрам» сайта Coca." />}
        columns={[
          { key: "v", header: "Заведение", mobile: "title", cell: (l) => <span className="font-medium">{l.venueName}</span> },
          { key: "n", header: "Контакт", mobile: "subtitle", cell: (l) => `${l.name} · ${l.phone}` },
          { key: "f", header: "Формат", cell: (l) => [LEAD_FORMAT_LABELS[l.format] ?? l.format, l.city].filter(Boolean).join(" · ") },
          { key: "b", header: "Филиалов", cell: (l) => l.branches },
          { key: "d", header: "Пришла", cell: (l) => dt(l.createdAt) },
          { key: "h", header: "Обработал", cell: (l) => l.handledByName ?? "—" },
          { key: "s", header: "Статус", cell: (l) => <Badge tone={LEAD_TONE[l.status]} dot>{LEAD_STATUS_LABELS[l.status]}</Badge> },
        ]} />
      {list.data && list.data.meta.total > 50 && <Pagination page={page} pageSize={50} total={list.data.meta.total} onPageChange={setPage} />}
      {open && <LeadDrawer key={open} id={open} onClose={() => setOpen(null)} />}
    </>
  );
}
